/**
 * LiveAudioStatsProbe — every 5 s read WebRTC audio stats (local mic sender +
 * every remote mic receiver) via livekit-client and POST per-window deltas to
 * /api/telemetry/audio. Numbers + enums only. Lives INSIDE <LiveKitRoom>.
 */
import { useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";
import { api } from "@/lib/api";

const INTERVAL_MS = 5000;
const OPUS_HZ = 48000;

const platform = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mobi/i.test(ua)) return "other";
  return "desktop";
};
const d = (cur, prev, k) => Math.max(0, (Number(cur?.[k]) || 0) - (Number(prev?.[k]) || 0));
const ms = (sec) => (sec == null ? null : Math.round(Number(sec) * 1000 * 10) / 10);

// Send loss comes from RTCP receiver reports that are NOT aligned to our 5 s
// window, so a single window can over/under-count. `loss_pct_30s` smooths the
// last 6 windows and is what "sustained loss" should be judged on.
function sendSample(cur, prev, lp, pub, enc, hist) {
  const sent = d(cur, prev, "packetsSent"), lost = d(cur, prev, "packetsLost");
  const dt = Math.max(0.001, ((cur.timestamp || 0) - (prev?.timestamp || 0)) / 1000);
  const bytes = d(cur, prev, "bytesSent");
  hist.push([sent, lost]); if (hist.length > 6) hist.shift();
  const hs = hist.reduce((a, x) => a + x[0], 0), hl = hist.reduce((a, x) => a + x[1], 0);
  return {
    dir: "send", packets: sent, packets_lost: lost, loss_pct: sent ? Math.min(100, (lost / sent) * 100) : 0,
    loss_pct_30s: hs ? Math.min(100, (hl / hs) * 100) : 0,
    jitter_ms: ms(cur.jitter), rtt_ms: ms(cur.roundTripTime), conn_quality: lp?.connectionQuality || "unknown", muted: !!pub?.isMuted,
    bitrate_kbps: prev ? Math.round((bytes * 8) / dt / 1000 * 10) / 10 : null,
    codec: enc?.codec || null, fmtp: enc?.fmtp || null, audio_level: enc?.level ?? null, capture: enc?.capture || null,
  };
}

// Capture settings + negotiated Opus params straight from the RTCRtpSender.
async function encodingInfo(track) {
  const out = { capture: null, codec: null, fmtp: null, level: null };
  try {
    const st = track?.mediaStreamTrack?.getSettings?.() || {};
    out.capture = { sample_rate: st.sampleRate || null, channels: st.channelCount || null, ec: st.echoCancellation ?? null,
      ns: st.noiseSuppression ?? null, agc: st.autoGainControl ?? null, device: (track?.mediaStreamTrack?.label || "").slice(0, 40) || null };
  } catch { /* noop */ }
  try {
    const sender = track?.sender;
    if (sender?.getStats) {
      const report = await sender.getStats();
      let codecId = null;
      report.forEach((r) => {
        if (r.type === "outbound-rtp") codecId = r.codecId || codecId;
        if (r.type === "media-source" && r.kind === "audio" && r.audioLevel != null) out.level = Math.min(1, Number(r.audioLevel));
      });
      if (codecId) { const c = report.get(codecId); if (c) { out.codec = `${c.mimeType || ""}/${c.clockRate || ""}`.replace(/^audio\//, ""); out.fmtp = (c.sdpFmtpLine || "").slice(0, 120) || null; } }
    }
  } catch { /* noop */ }
  return out;
}

// Full inbound-rtp report (livekit's getReceiverStats() omits packetsReceived /
// packetsLost / silentConcealed*, which made every receiver look like "0% loss").
async function inboundStats(track) {
  try {
    const rx = track?.receiver;
    if (!rx?.getStats) return null;
    const report = await rx.getStats();
    let out = null;
    report.forEach((r) => { if (r.type === "inbound-rtp" && (r.kind === "audio" || !r.kind)) out = r; });
    return out;
  } catch { return null; }
}

function recvSample(cur, prev, p, pub) {
  const recv = d(cur, prev, "packetsReceived"), lost = d(cur, prev, "packetsLost");
  const energy = d(cur, prev, "totalAudioEnergy"), dur = d(cur, prev, "totalSamplesDuration");
  const concealed = Math.round((d(cur, prev, "concealedSamples") / OPUS_HZ) * 1000);
  const silent = Math.round((d(cur, prev, "silentConcealedSamples") / OPUS_HZ) * 1000);
  const jbEmitted = d(cur, prev, "jitterBufferEmittedCount");
  return {
    dir: "recv", remote_user_id: p.identity, packets: recv, packets_lost: lost,
    loss_pct: recv + lost ? (lost / (recv + lost)) * 100 : 0,
    jitter_ms: ms(cur.jitter), concealment_events: d(cur, prev, "concealmentEvents"),
    silent_concealment_events: d(cur, prev, "silentConcealmentEvents"),
    concealed_ms: concealed, silent_concealed_ms: silent, audible_concealed_ms: Math.max(0, concealed - silent),
    accel_ms: Math.round((d(cur, prev, "removedSamplesForAcceleration") / OPUS_HZ) * 1000),
    decel_ms: Math.round((d(cur, prev, "insertedSamplesForDeceleration") / OPUS_HZ) * 1000),
    jb_delay_ms: jbEmitted ? Math.round((d(cur, prev, "jitterBufferDelay") / jbEmitted) * 1000) : null,
    audio_level: dur > 0 ? Math.min(1, Math.sqrt(energy / dur)) : null, conn_quality: p.connectionQuality || "unknown", muted: !!pub?.isMuted,
  };
}

export default function LiveAudioStatsProbe({ streamId, role }) {
  const room = useRoomContext();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let on = true;
    api.get("/telemetry/audio/enabled").then(({ data }) => on && setEnabled(!!data?.enabled)).catch(() => {});
    return () => { on = false; };
  }, []);

  useEffect(() => {
    if (!room || !streamId || !enabled) return undefined;
    let sid = null;
    try { sid = sessionStorage.getItem(`pandax_live_sid_${streamId}`); } catch { /* noop */ }
    if (!sid) sid = `audio-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
    const prev = new Map();
    const sendHist = [];
    let lastTick = Date.now();
    let alive = true;
    const events = [];
    const ev = (kind, who, detail) => { if (events.length < 40) events.push({ at: new Date().toISOString(), kind, who: who || null, detail: detail || null }); };
    const isMic = (pub) => pub?.source === Track.Source.Microphone || pub?.kind === "audio";
    const handlers = {
      [RoomEvent.TrackMuted]: (pub, p) => isMic(pub) && ev("mic_muted", p?.identity),
      [RoomEvent.TrackUnmuted]: (pub, p) => isMic(pub) && ev("mic_unmuted", p?.identity),
      [RoomEvent.TrackPublished]: (pub, p) => isMic(pub) && ev("mic_published", p?.identity),
      [RoomEvent.TrackUnpublished]: (pub, p) => isMic(pub) && ev("mic_unpublished", p?.identity),
      [RoomEvent.LocalTrackPublished]: (pub) => isMic(pub) && ev("mic_published", room.localParticipant?.identity, "local"),
      [RoomEvent.LocalTrackUnpublished]: (pub) => isMic(pub) && ev("mic_unpublished", room.localParticipant?.identity, "local"),
      [RoomEvent.ParticipantConnected]: (p) => ev("participant_joined", p?.identity),
      [RoomEvent.ParticipantDisconnected]: (p) => ev("participant_left", p?.identity),
      [RoomEvent.ParticipantPermissionsChanged]: (_prev, p) => ev("role", p?.identity, p?.permissions?.canPublish ? "can_publish" : "listen_only"),
      [RoomEvent.Reconnecting]: () => ev("reconnecting", room.localParticipant?.identity),
      [RoomEvent.Reconnected]: () => ev("reconnected", room.localParticipant?.identity),
      [RoomEvent.AudioPlaybackStatusChanged]: () => ev("playback", room.localParticipant?.identity, room.canPlaybackAudio ? "allowed" : "blocked"),
    };
    Object.entries(handlers).forEach(([k, fn]) => room.on(k, fn));
    ev("role", room.localParticipant?.identity, role || null);

    const tick = async () => {
      if (!alive || room.state !== "connected") return;
      const samples = [];
      const lp = room.localParticipant;
      const micPub = lp?.getTrackPublication?.(Track.Source.Microphone);
      // Stats are cumulative per track: the FIRST read only seeds `prev` (an
      // un-seeded delta would report everything since connect as one window),
      // and the key includes the media track id so a re-published / re-subscribed
      // track (new counters) starts a fresh baseline instead of a bogus delta.
      if (micPub?.track?.getSenderStats) {
        const cur = await micPub.track.getSenderStats().catch(() => null);
        const key = `send|${micPub.track.mediaStreamTrack?.id || ""}`;
        if (cur) {
          const pv = prev.get(key);
          if (pv) { const enc = await encodingInfo(micPub.track); samples.push(sendSample(cur, pv, lp, micPub, enc, sendHist)); }
          prev.set(key, cur);
        }
      }
      for (const p of room.remoteParticipants.values()) {
        const pub = p.getTrackPublication?.(Track.Source.Microphone);
        if (!pub?.track) continue;
        const cur = (await inboundStats(pub.track)) || (pub.track.getReceiverStats ? await pub.track.getReceiverStats().catch(() => null) : null);
        const key = `${p.identity}|${pub.track.mediaStreamTrack?.id || pub.trackSid || ""}`;
        if (cur) {
          const pv = prev.get(key);
          if (pv) samples.push(recvSample(cur, pv, p, pub));
          prev.set(key, cur);
        }
      }
      const now = Date.now();
      const windowMs = now - lastTick;
      lastTick = now;
      if ((!samples.length && !events.length) || !alive) return;
      const flush = events.splice(0, 20);
      api.post("/telemetry/audio", {
        stream_id: streamId, session_correlation_id: sid, role: role || null, platform: platform(),
        net_type: navigator.connection?.effectiveType || null, livekit_state: String(room.state || ""), window_ms: windowMs,
        samples: samples.slice(0, 20), events: flush,
      }).catch(() => {});
    };

    const t = setInterval(() => { tick().catch(() => {}); }, INTERVAL_MS);
    return () => { alive = false; clearInterval(t); Object.entries(handlers).forEach(([k, fn]) => room.off(k, fn)); };
  }, [room, streamId, role, enabled]);

  return null;
}
